import { lazy, Suspense } from 'react';
import { Navigate, Routes, Route, useLocation } from 'react-router';
import { useCharactersInit } from '@/hooks/useCharacter';
import { useTranslation } from '@/i18n';
import MainShell from './components/MainShell';

function RootLayout() {
  return <MainShell />;
}

const CharacterWizard = lazy(() => import('./components/CharacterWizard'));
const CharacterViewPage = lazy(() => import('./components/CharacterViewPage'));
const MyCharacters = lazy(() => import('./pages/MyCharacters'));
const KnowledgeBasePage = lazy(() => import('./pages/KnowledgeBasePage'));
const EncounterBuilder = lazy(() => import('./pages/EncounterBuilder'));
const InitiativeTracker = lazy(() => import('./pages/InitiativeTracker'));
const TreasurePlanner = lazy(() => import('./pages/TreasurePlanner'));
const GMHelp = lazy(() => import('./pages/GMHelp'));
const GMScreenWorkspacePage = lazy(() => import('./pages/GMScreenWorkspacePage'));
const LoginPage = lazy(() => import('./pages/auth/LoginPage'));
const RegisterForm = lazy(() => import('./pages/auth/RegisterForm'));
const ForgotPasswordPage = lazy(() => import('./pages/auth/ForgotPasswordPage'));
const ForcePasswordChangePage = lazy(() => import('./pages/auth/ForcePasswordChangePage'));
const RequiredTwoFactorSetupPage = lazy(() => import('./pages/auth/RequiredTwoFactorSetupPage'));
const AdminLayout = lazy(() => import('./pages/admin/AdminLayout'));
const AdminDashboard = lazy(() => import('./pages/admin/AdminDashboard'));
const AdminUsers = lazy(() => import('./pages/admin/AdminUsers'));
const AdminLogs = lazy(() => import('./pages/admin/AdminLogs'));
const AdminBackups = lazy(() => import('./pages/admin/AdminBackups'));
const UserLayout = lazy(() => import('./pages/user/UserLayout'));
const UserSettings = lazy(() => import('./pages/user/UserSettings'));
const LocalizationLayout = lazy(() => import('./pages/localization/LocalizationLayout'));
const LocalizationStatistics = lazy(() => import('./pages/localization/LocalizationStatistics'));
const LocalizationEditor = lazy(() => import('./pages/localization/LocalizationEditor'));
const LocalizationConfirmation = lazy(() => import('./pages/localization/LocalizationConfirmation'));
const UiDemoPage = lazy(() => import('./components/ui/demo/UiDemoPage'));
const LegalPage = lazy(() => import('./pages/legal/LegalPage'));
const CompanionKindSelectionPage = lazy(() => import('./pages/CompanionKindSelectionPage'));
const CompanionWizardPage = lazy(() => import('./pages/CompanionsPage').then(module => ({ default: module.CompanionWizardPage })));
const CompanionDetailPage = lazy(() => import('./pages/CompanionsPage').then(module => ({ default: module.CompanionDetailPage })));
const CompanionSetupPage = lazy(() => import('./pages/CompanionSetupPage'));

function AppWithLocation() {
  const location = useLocation();
  const t = useTranslation();
  const wizardKey = `wizard-${location.pathname}`;

  // Bootstrap character data once for the entire app lifetime.
  // Lives here (not in CharacterList) so it never remounts on navigation.
  useCharactersInit();

  return (
    <Suspense fallback={<div className="loading">{t('common.loading', {}, 'Loading...')}</div>}>
      <Routes>
        {/* Auth Routes */}
        <Route path="/login" element={<LoginPage />} />
        <Route path="/register" element={<RegisterForm />} />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/force-password-change" element={<ForcePasswordChangePage />} />
        <Route path="/force-2fa-setup" element={<RequiredTwoFactorSetupPage />} />

        {/* Focused GM-screen workspace without the regular application sidebar. */}
        <Route path="/gm-help/screen/draft/edit/*" element={<GMScreenWorkspacePage />} />
        <Route path="/gm-help/screen/layouts/:layoutId/edit/*" element={<GMScreenWorkspacePage />} />

        {/* Main App Routes with Sidebar */}
        <Route path="/" element={<RootLayout />}>
          <Route index element={<MyCharacters />} />
          <Route path="characters" element={<MyCharacters />} />
          <Route path="knowledge" element={<KnowledgeBasePage />} />
          <Route path="knowledge/:targetType/:slug" element={<KnowledgeBasePage />} />
          <Route path="create" element={<CharacterWizard key={wizardKey} />} />
          <Route path="companions" element={<Navigate to="/" replace />} />
          <Route path="companions/new" element={<CompanionKindSelectionPage />} />
          <Route path="companions/new/:kind" element={<CompanionWizardPage />} />
          <Route path="companions/:entityId" element={<CompanionDetailPage />} />
          <Route path="characters/:characterId/companions/setup" element={<CompanionSetupPage />} />
          <Route path=":id" element={<CharacterViewPage />} />
          <Route path=":id/edit" element={<CharacterWizard key={wizardKey} />} />
          <Route path="encounter-builder" element={<EncounterBuilder />} />
          <Route path="initiative-tracker" element={<InitiativeTracker />} />
          <Route path="treasure-planner" element={<TreasurePlanner />} />
          <Route path="gm-help" element={<GMHelp />} />
          <Route path="legal" element={<Navigate to="/legal/privacy" replace />} />
          <Route path="legal/:document" element={<LegalPage />} />
        </Route>

        {/* Admin Panel Routes */}
        <Route path="/admin" element={<AdminLayout />}>
          <Route index element={<AdminDashboard />} />
          <Route path="users" element={<AdminUsers />} />
          <Route path="logs" element={<AdminLogs />} />
          <Route path="backups" element={<AdminBackups />} />
          <Route path="ui-library" element={<UiDemoPage />} />
        </Route>

        {/* Localization Panel Routes */}
        <Route path="/localization" element={<LocalizationLayout />}>
          <Route index element={<LocalizationStatistics />} />
          <Route path="editor" element={<LocalizationEditor />} />
          <Route path="confirmation" element={<LocalizationConfirmation />} />
        </Route>

        {/* User Panel Routes */}
        <Route path="/user" element={<UserLayout />}>
          <Route index element={<UserSettings />} />
        </Route>

        <Route path="/ui-demo" element={<Navigate to="/" replace />} />
      </Routes>
    </Suspense>
  );
}

function App() {
  return <AppWithLocation />;
}

export default App;
